import React, { useCallback, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { differenceBy } from "lodash";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { useFuzzySearch } from "@hooks/use-fuzzy-search";
import { type CategoricalBreakdownConfig } from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";
import { Checkbox } from "@shared/checkbox";
import { useVirtualizer } from "@tanstack/react-virtual";

import { type TopLevelBreakdown } from "../../types";

interface Props {
  breakdown: TopLevelBreakdown;
  onUpdate(values: string[]): void;
  onDelete(): void;
}

export const BreakdownValueSelectorContent = ({
  breakdown,
  onUpdate,
  onDelete,
}: Props) => {
  const scrollElRef = useRef<HTMLDivElement>(null);

  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const breakdownConfig = useMemo(
    () =>
      breakdownConfigs.find((e) => e.key === breakdown.key) as
        | CategoricalBreakdownConfig
        | undefined,
    [breakdown, breakdownConfigs],
  );

  const [aboutToDelete, setAboutToDelete] = useState(false);
  const [selectedValues, setSelectedValues] = useState(breakdown.values);

  const [filteredOptions, { search, setSearch }] = useFuzzySearch(
    breakdownConfig?.choices || ([] as CategoricalBreakdownConfig["choices"]),
    {
      searchKeys: ["label"],
    },
  );

  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollElRef.current,
    estimateSize: () => 32,
    overscan: 5,
  });

  const isAllSelected =
    differenceBy(
      breakdownConfig?.choices.map((e) => e.value),
      selectedValues,
    ).length === 0;

  const selectAllRef = useCallback(
    (node: HTMLInputElement) => {
      if (node !== null) {
        node.indeterminate = selectedValues.length > 0 && !isAllSelected;
      }
    },
    [selectedValues, isAllSelected],
  );

  if (!breakdownConfig) {
    return null;
  }

  return (
    <>
      <h4 className="text-sm font-bold mx-0 mt-0 mb-2.5 px-4">
        {breakdownConfig.name} ({breakdown.values.length} of{" "}
        {breakdownConfig.choices.length})
      </h4>
      <div className="nb-input-container relative px-4 mb-2">
        <input
          className="nb-input pr-8"
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <i
          className={clsx(
            "fa-regular fa-sharp fa-magnifying-glass text-[0.9rem] text-primary",
            "w-4 absolute right-[24px] top-[10px]",
          )}
        ></i>
      </div>
      {filteredOptions.length === 0 ? (
        <div className="px-4 py-2 text-disabled">NO RESULTS FOUND</div>
      ) : (
        <>
          <div
            className="px-4 hover:bg-cosmic-4 h-8 flex items-center cursor-pointer relative"
            onClick={() =>
              setSelectedValues(
                isAllSelected
                  ? []
                  : breakdownConfig.choices.map((e) => e.value),
              )
            }
          >
            <Checkbox
              ref={selectAllRef}
              className="mr-2"
              inputClassName={clsx(
                "before:h-[inherit] before:w-[inherit] after:top-[15px] after:left-[18px]",
              )}
              value={isAllSelected}
            />
            <span className="font-bold">
              {isAllSelected ? "Clear all" : "Select all"}
            </span>
          </div>
          <div ref={scrollElRef} className="max-h-[300px] overflow-y-auto">
            <div
              style={{
                position: "relative",
                height: `${rowVirtualizer.getTotalSize()}px`,
              }}
            >
              {rowVirtualizer.getVirtualItems().map((e) => {
                const row = filteredOptions[e.index];

                return (
                  <div
                    key={e.key}
                    className={clsx(
                      "px-4 hover:bg-cosmic-4 h-8 w-full flex items-center cursor-pointer",
                      "absolute top-0 left-0",
                    )}
                    style={{ transform: `translateY(${e.start}px)` }}
                    onClick={() => {
                      if (selectedValues.includes(row.value)) {
                        setSelectedValues(
                          selectedValues.filter((v) => v !== row.value),
                        );
                      } else {
                        setSelectedValues([...selectedValues, row.value]);
                      }
                    }}
                  >
                    <Checkbox
                      className="mr-2"
                      value={selectedValues.includes(row.value)}
                    />
                    {row.label}
                  </div>
                );
              })}
            </div>
          </div>
          <div
            className={clsx(
              "border-t order-gray-1 px-4 pt-2 pb-2 h-11 rounded-b-lg",
              "flex items-center justify-center",
              { "bg-mars-2": aboutToDelete },
            )}
          >
            {aboutToDelete ? (
              <div className="w-full flex items-center justify-between">
                Delete filter?
                <div className="space-x-1">
                  <button
                    className="text-xs font-bold text-mars px-2 py-1 hover:bg-white rounded"
                    onClick={onDelete}
                  >
                    YES
                  </button>
                  <button
                    className="text-xs font-bold text-primary px-2 py-1 hover:bg-white rounded"
                    onClick={() => setAboutToDelete(false)}
                  >
                    NO
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-x-2">
                <PrimaryButton
                  className="bg-white text-mars font-bold hover:bg-mars-2"
                  onClick={() => setAboutToDelete(true)}
                >
                  DELETE FILTER
                </PrimaryButton>
                <PrimaryButton
                  className="font-bold"
                  onClick={() => {
                    if (selectedValues.length === 0) {
                      onDelete();
                    } else {
                      onUpdate(selectedValues);
                    }
                  }}
                >
                  UPDATE
                </PrimaryButton>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};
